<template>
  <CustomDropdown
    ref="dropdownRef"
    maxHeight="300px"
    gap="8px"
    position="start"
    dropdownCSS="p-0"
  >
    <template #trigger>
      <div
        class="flex items-center gap-1.5 px-3 py-1 rounded-lg cursor-pointer hover:bg-[--white-opacity-4]"
        :class="{ 'bg-[--white-opacity-4]': isDropdownOpen }"
      >
        <ThemeImage
          :icon="
            showObjectSelector ? imageNames.FILTER_GREY : imageNames.PLUS_GREY
          "
          alt="filter_icon"
          class="size-4"
        />
        <span class="proxima_nova text-sm font-semibold secondary_color">{{
          buttonText
        }}</span>
      </div>
    </template>

    <template #default>
      <div class="flex flex-col w-[266px]">
        <div
          v-if="showObjectSelector"
          class="flex flex-col py-3 px-2 border_bottom"
          @click.stop=""
        >
          <a-select
            :value="selectedObjectId"
            class="w-full"
            placeholder="Select object"
            show-search
            :loading="loadingObjects"
            :filter-option="filterObjectOption"
            :get-popup-container="getPopupContainer"
            @change="handleObjectChange"
            @click.stop=""
            @mousedown.stop=""
          >
            <a-select-option
              v-for="obj in availableObjects"
              :key="obj.id"
              :value="obj.id"
            >
              {{ obj.plural_name || obj.name }}
            </a-select-option>

            <template #suffixIcon>
              <ThemeImage
                :icon="imageNames.BOTTOM_ARROW"
                alt="dropdown_icon"
                class="size-4"
              />
            </template>
          </a-select>
        </div>

        <div class="flex flex-col py-3 px-2 border_bottom">
          <a-input
            class="input_medium"
            v-model:value="searchQuery"
            placeholder="Search Property"
            allow-clear
            autofocus
            @click.stop=""
            @keydown.stop="$event.stopPropagation()"
          >
            <template #prefix>
              <ThemeImage
                :icon="imageNames.SEARCH_VARIANT1"
                alt="search_icon"
                class="size-4"
              />
            </template>
          </a-input>
        </div>

        <div class="flex flex-col p-2 gap-1 max-h-[200px] overflow-y-auto">
          <div v-if="isLoading" class="w-full">
            <SkeletonLoader page="nav_list" :containerHeight="'h-[200px]'" />
          </div>

          <template v-else-if="filteredOptions.length > 0">
            <div
              v-for="(option, index) in filteredOptions"
              :key="`custom_filter_option_${option.id ?? index}`"
              class="dropdown_item flex items-center justify-between flex-row gap-2 px-3 py-2 rounded-lg cursor-pointer flex-shrink-0"
              @click="onOptionClick(option)"
            >
              <span class="text-sm truncate">{{ option.name }}</span>
            </div>
          </template>

          <div v-else class="w-full">
            <EmptyScreen
              source="filters_empty"
              text="No results found!"
              padding="my-6"
              css="size-16"
            />
          </div>
        </div>
      </div>
    </template>
  </CustomDropdown>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import ThemeImage from "@/src/components/ThemeImage.vue";
import CustomDropdown from "@/src/components/CustomDropdown.vue";
import EmptyScreen from "@/src/components/EmptyScreen.vue";
import SkeletonLoader from "@/src/components/SkeletonLoader.vue";
import { imageNames } from "@/src/constants/imageConstants.js";
import { defaultFilterOptions } from "@/src/constants/crmConstants.js";
import { filterOutExcluded } from "@/src/utils/crmFilterUtils.js";

const props = defineProps({
  filtersOptions: { type: Array, default: () => [] },
  dynamicFilterOptions: { type: Array, default: () => [] },
  isDynamicCRM: { type: Boolean, default: false },
  searchable: { type: Boolean, default: true },
  buttonText: { type: String, default: "Filters" },
  isLoading: { type: Boolean, default: false },
  showObjectSelector: { type: Boolean, default: false },
  availableObjects: { type: Array, default: () => [] },
  selectedObjectId: { type: String, default: null },
  loadingObjects: { type: Boolean, default: false },
  // Sequential-id objects expose `id` as a filterable column; UUID-backed
  // objects keep it hidden because their ids aren't human-memorable.
  allowIdFilter: { type: Boolean, default: false },
});

const emit = defineEmits(["select-option", "search", "object-change", "close"]);

const dropdownRef = ref(null);
const searchQuery = ref("");
const isDropdownOpen = ref(false);

const filteredOptions = computed(() => {
  let optionsToUse = [];
  if (
    props.filtersOptions.length > 0 &&
    props.dynamicFilterOptions.length > 0
  ) {
    const staticFiltersMap = new Map();
    props.filtersOptions.forEach((filter) => {
      staticFiltersMap.set(filter.id, filter);
      // Also map by attribute_id for matching renamed fields
      if (filter.attribute_id && filter.attribute_id !== filter.id) {
        staticFiltersMap.set(filter.attribute_id, filter);
      }
    });

    // Track which static filters have been used
    const usedStaticFilterIds = new Set();

    const mergedFilters = [];
    props.dynamicFilterOptions.forEach((dynamicFilter) => {
      // Check both id and attribute_id to find matching static filter
      const staticFilter = staticFiltersMap.get(dynamicFilter.id) || staticFiltersMap.get(dynamicFilter.attribute_id);

      if (
        staticFilter &&
        staticFilter.options &&
        staticFilter.options.length > 0
      ) {
        mergedFilters.push(staticFilter);
        usedStaticFilterIds.add(staticFilter.id);
      } else {
        mergedFilters.push(dynamicFilter);
      }
    });

    // Add static filters that weren't matched with any dynamic filter
    props.filtersOptions.forEach((filter) => {
      if (!usedStaticFilterIds.has(filter.id)) {
        mergedFilters.push(filter);
      }
    });

    optionsToUse = mergedFilters;
  } else if (props.dynamicFilterOptions.length > 0) {
    optionsToUse = props.dynamicFilterOptions;
  } else if (props.filtersOptions.length > 0) {
    optionsToUse = props.filtersOptions;
  } else if (!props.isDynamicCRM) {
    optionsToUse = defaultFilterOptions;
  }

  const cleaned = filterOutExcluded(optionsToUse, {
    allowIdFilter: props.allowIdFilter,
  });

  if (!props.searchable || !searchQuery.value.trim()) {
    return cleaned;
  }

  const query = searchQuery.value.toLowerCase().trim();
  return cleaned.filter((opt) => opt?.name?.toLowerCase().includes(query));
});

const onOptionClick = (option) => {
  dropdownRef.value?.closeDropdown?.();
  emit("select-option", option);
};

watch(searchQuery, (newValue) => {
  emit("search", newValue);
});

watch(
  () => dropdownRef.value?.isOpen,
  (newValue, oldValue) => {
    if (newValue !== undefined) {
      isDropdownOpen.value = newValue;
      if (!newValue && oldValue === true) {
        emit("close");
        searchQuery.value = "";
      }
    }
  },
);

const handleObjectChange = (objectId) => {
  emit("object-change", objectId);
};

const getPopupContainer = (triggerNode) => {
  return triggerNode.parentNode || document.body;
};

const filterObjectOption = (input, option) => {
  const obj = props.availableObjects.find((o) => o.id === option.value);
  const text = obj?.plural_name || obj?.name || "";
  return text.toLowerCase().includes(input.toLowerCase());
};

defineExpose({
  closeDropdown: () => dropdownRef.value?.closeDropdown?.(),
  get isOpen() {
    return dropdownRef.value?.isOpen;
  },
});
</script>
